import React from 'react'
import { CSSTransition, SwitchTransition } from 'react-transition-group'

import usePropertyCompareFirebaseTrigger from '@components/compare-property/utils'

import { CompareSvg, StopCompareSvg } from '@svg/react'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { RequiredComparableProperty } from '@adUtilities/types'

interface ComparePropertyToggleActionProps {
  property: RequiredComparableProperty['property']
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  hide: boolean
}

function ComparePropertyToggleAction({
  comparePropertyProps,
  property,
  hide,
}: ComparePropertyToggleActionProps) {
  const {
    checkPropertyComparability,
    comparableProperties,
    hasEmptyComparableSlot,
  } = comparePropertyProps
  const { triggerComparisonStart, triggerComparisonStop } =
    usePropertyCompareFirebaseTrigger({ comparePropertyProps })
  const { compareModeIsActive } = comparePropertyProps

  const handleToggleCompareMode = React.useCallback(() => {
    if (!compareModeIsActive) {
      triggerComparisonStart({ property })
      return
    }
    triggerComparisonStop()
  }, [
    compareModeIsActive,
    triggerComparisonStart,
    triggerComparisonStop,
    property,
  ])

  const isComparable = React.useMemo<boolean>(
    () => !hide && checkPropertyComparability(property),
    [checkPropertyComparability, hide, property]
  )

  const { buttonText, buttonClassName, iconClassName } = React.useMemo<{
    buttonText: string
    buttonClassName: string
    iconClassName: string
  }>(() => {
    if (compareModeIsActive && hasEmptyComparableSlot) {
      return {
        buttonText: 'Select to compare',
        buttonClassName: 'text-mainColour bg-neutralColour',
        iconClassName: 'stroke-mainColour',
      }
    }
    if (compareModeIsActive) {
      return {
        buttonText: 'Stop comparing',
        buttonClassName: 'text-[#FF000099]/60 bg-neutralColour',
        iconClassName: 'fill-[#FF000099]/60 stroke-[#FF000099]/60',
      }
    }
    return {
      buttonText: 'Compare',
      buttonClassName: 'text-neutralColour bg-gradient',
      iconClassName: 'stroke-neutralColour',
    }
  }, [compareModeIsActive, comparableProperties])

  if (!isComparable) {
    return null
  }

  return (
    <SwitchTransition>
      <CSSTransition
        classNames="transition-property-compare-toggle-action"
        timeout={100}
        key={buttonText}
      >
        <button
          type="button"
          className={`flex items-center justify-center gap-1 rounded-[5px] px-[15px] py-[7px] text-center text-xl font-semibold uppercase ${buttonClassName}`}
          onClick={handleToggleCompareMode}
        >
          {buttonText}
          {compareModeIsActive && !hasEmptyComparableSlot ? (
            <StopCompareSvg className={`h-7 w-7 ${iconClassName}`} />
          ) : (
            <CompareSvg className={`h-7 w-7 ${iconClassName}`} />
          )}
        </button>
      </CSSTransition>
    </SwitchTransition>
  )
}

export default ComparePropertyToggleAction
